import { DataTable } from '@/components/features/shared/DataTable'
import { Badge } from '@/components/ui/badge'
import { IconButton } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { PaginatedResponse } from '@/domain'
import { Movement, formatMovementStatus } from '@/domain/accounts/account'
import { ColumnDef, PaginationState } from '@tanstack/react-table'
import { Trash2Icon } from 'lucide-react'
import { RenderAmount, RenderBalance } from './account-utils'

interface MovementsTableProps {
  paginated: PaginatedResponse<Movement> | undefined
  isLoading?: boolean
  pagination?: PaginationState
  onPaginationChange?: React.Dispatch<React.SetStateAction<PaginationState>>
  onDeleteRequest?: (movement: Movement) => void
}

export const MovementsTable = ({
  paginated,
  isLoading,
  pagination,
  onPaginationChange,
  onDeleteRequest,
}: MovementsTableProps) => {
  const data = paginated?.data || []

  const columns: ColumnDef<Movement>[] = [
    {
      accessorKey: 'description',
      header: 'Descripcion',
      cell: ({ row }) => {
        const movement = row.original
        return (
          <div className="flex flex-col">
            <span className="flex items-center gap-2">
              {movement.status !== 'completed' && (
                <Badge variant="outline">{formatMovementStatus(movement.status as any)}</Badge>
              )}
              <span className="font-medium">{movement.description}</span>
            </span>
            <span className="text-xs text-muted-foreground">
              {movement.movementDate && (typeof movement.movementDate === 'string' ? new Date(movement.movementDate) : movement.movementDate).toLocaleDateString('es-AR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: 'amount',
      header: 'Monto',
      cell: ({ row }) => {
        const movement = row.original
        return <RenderAmount amount={movement.amount} movementType={movement.type} />
      },
    },
    {
      accessorKey: 'balanceAfter',
      header: 'Saldo',
      cell: ({ row }) => {
        const movement = row.original
        return <RenderBalance balance={movement.balanceAfter} />
      },
    },
    {
      id: 'actions',
      header: 'Acciones',
      cell: ({ row }) => {
        const movement = row.original
        return (
          <div className="flex items-center gap-2">
            {onDeleteRequest && (
              <IconButton
                variant="danger"
                onClick={(e) => {
                  e.stopPropagation()
                  onDeleteRequest(movement)
                }}
              >
                <Trash2Icon className="h-3.5 w-3.5" />
              </IconButton>
            )}
          </div>
        )
      },
    },
  ]

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    )
  }

  return (
    <DataTable
      columns={columns}
      data={data}
      pageCount={paginated?.totalPages}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      totalRecords={paginated?.total}
    />
  )
}
